import { createFileRoute } from "@tanstack/react-router";
import { useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import ComponentLibraryLayout from "ui/library/ComponentLibraryLayout";
import type { ComponentRegistryEntry } from "ui/library/componentRegistry";
import { staticRegistry, loadDynamicComponents } from "ui/library/componentRegistry";
import withAuthentication from "../../../components/withAuthentication";

function ComponentLibraryPage() {
  const searchParams = useSearch({ strict: false });
  const { id } = searchParams as { id?: string };
  const [runtimeRegistry, setRuntimeRegistry] = useState<Record<string, ComponentRegistryEntry>>(staticRegistry);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadComponents = (): void => {
      try {
        const dynamicComponents = loadDynamicComponents();
        setRuntimeRegistry({
          ...staticRegistry,
          ...dynamicComponents, // Dynamic components override placeholders
        });
      } catch (error) {
        console.warn("Failed to load dynamic components:", error);
        // Fall back to static registry
        setRuntimeRegistry(staticRegistry);
      }
      setIsLoading(false);
    };

    loadComponents();
  }, []);

  // If no id is provided, show the overview (empty string key)
  const componentId = (id as string) || "";
  const registryEntry = runtimeRegistry[componentId];

  if (isLoading) {
    return (
      <ComponentLibraryLayout headerText="Loading..." subheaderText="" externalLinks={[]}>
        <div>Loading component...</div>
      </ComponentLibraryLayout>
    );
  }

  if (!registryEntry) {
    return (
      <ComponentLibraryLayout headerText="Component Not Found" subheaderText="" externalLinks={[]}>
        <div>Component &quot;{componentId}&quot; not found</div>
      </ComponentLibraryLayout>
    );
  }

  return <ComponentPage registryEntry={registryEntry} />;
}

export function ComponentPage({ registryEntry }: { registryEntry: ComponentRegistryEntry }) {
  const ComponentContent = registryEntry.component;

  return (
    <ComponentLibraryLayout
      headerText={registryEntry.name}
      subheaderText={registryEntry.subheader}
      externalLinks={registryEntry.externalLinks}
    >
      <ComponentContent />
    </ComponentLibraryLayout>
  );
}

export const Route = createFileRoute("/admin/component-library/")({
  component: withAuthentication(ComponentLibraryPage, { superAdmin: true }),
});
